import * as React from "react";
import { Button, CircularProgress, Typography } from "@mui/material";
import { DateTime } from "luxon";
import {
  claimOrderProductionJobCustomizationAreaMutation,
  unclaimOrderProductionJobCustomizationAreaMutation
} from "../../graph";
import { makeStyles } from "@mui/styles";
import { query } from "./graph";
import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import PendingStatusView from "../PendingStatusView";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import Tooltip from "@mui/material/Tooltip";
import withSnackbar from "../withSnackbar";

const useStyles = makeStyles(theme => ({
  claimedByContainer: {
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.secondary.main}`,
    display: "flex",
    justifyContent: "space-between",
    paddingTop: theme.spacing(1)
  },

  claimedByLabel: {
    fontWeight: theme.typography.fontWeightMedium
  },

  progressBarContainer: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0.5),
    width: "100%"
  }
}));

const CustomizationAreaClaimDetails = ({
  customizationAreaId,
  notesCollection,
  showErrorSnackbar,
  showSuccessSnackbar
}) => {
  const classes = useStyles();

  const [isInProgress, setIsInProgress] = useState(false);

  const [claimOrderProductionJobCustomizationArea] = useMutation(
    claimOrderProductionJobCustomizationAreaMutation
  );
  const [unclaimOrderProductionJobCustomizationArea] = useMutation(
    unclaimOrderProductionJobCustomizationAreaMutation
  );

  const { data, error, loading, refetch } = useQuery(query, {
    variables: { customizationAreaId }
  });

  const notesCollectionComponent =
    notesCollection && notesCollection.length > 0 ? (
      <NotesTooltipTitle notesCollection={notesCollection} />
    ) : null;

  const handleClaimButtonClick = () => {
    setIsInProgress(true);

    claimOrderProductionJobCustomizationArea({
      variables: {
        orderProductionJobCustomizationAreaId: customizationAreaId
      }
    })
      .then(
        ({
          data: {
            claimOrderProductionJobCustomizationArea: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`Customization area has been claimed.`);
            refetch();
          } else {
            showErrorSnackbar(
              `Something went wrong. Customization Area ${errors.orderProductionJobCustomizationAreaId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsInProgress(false));
  };

  const handleUnclaimButtonClick = () => {
    setIsInProgress(true);

    unclaimOrderProductionJobCustomizationArea({
      variables: {
        orderProductionJobCustomizationAreaId: customizationAreaId
      }
    })
      .then(
        ({
          data: {
            unclaimOrderProductionJobCustomizationArea: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`Customization area has been unclaimed.`);
            refetch();
          } else {
            showErrorSnackbar(
              `Something went wrong. Customization Area ${errors.orderProductionJobCustomizationAreaId.join(", ")}`
            );
          }
        }
      )
      .finally(() => setIsInProgress(false));
  };

  if (!data || error) {
    return <PendingStatusView status="Waiting" />;
  }

  if (loading) {
    return <PendingStatusView status="Loading" />;
  }

  if (data.orderProductionJobCustomizationArea) {
    const {
      canViewerClaim,
      lastClaimedBy,
      orderProductionJobCustomizationAreaClaim
    } = data.orderProductionJobCustomizationArea;

    if (lastClaimedBy) {
      return (
        <div className={classes.claimedByContainer}>
          <Typography className={classes.claimedByLabel}>
            Last claimed By:
          </Typography>
          <Typography>{lastClaimedBy.name}</Typography>
        </div>
      );
    }

    const claimedEmployeeName = orderProductionJobCustomizationAreaClaim
      ? orderProductionJobCustomizationAreaClaim.employee.name
      : null;

    return (
      <div className={classes.claimedByContainer}>
        {isInProgress ? (
          <div className={classes.progressBarContainer}>
            <CircularProgress size={30} />
          </div>
        ) : claimedEmployeeName ? (
          <div>
            <Typography className={classes.claimedByText} variant="subtitle2">
              Claimed by: {claimedEmployeeName}
            </Typography>
            <Button
              color="primary"
              onClick={handleUnclaimButtonClick}
              variant="contained"
            >
              Unclaim
            </Button>
          </div>
        ) : (
          <Button
            color="primary"
            disabled={!canViewerClaim}
            onClick={handleClaimButtonClick}
            variant="contained"
          >
            Claim
          </Button>
        )}
        {notesCollectionComponent && (
          <Tooltip title={notesCollectionComponent} placement="top-start" arrow>
            <IconButton>
              <StickyNote2Icon color="primary" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  } else {
    return null;
  }
};

const NotesTooltipTitle = ({ notesCollection }) => (
  <div>
    <h3 style={{ margin: 0 }}>Art Notes:</h3>
    <ul
      style={{
        paddingLeft: "10px"
      }}
    >
      {notesCollection.map((item, index) => {
        const { __typename } = item;

        if (__typename === "FormattedNote") {
          const { author, note, createdAt } = item;
          const timestamp = DateTime.fromISO(createdAt).toLocaleString(
            DateTime.DATETIME_MED
          );

          return <li key={index}>{`${author} (${timestamp}): ${note}`}</li>;
        } else if (__typename === "UnformattedNote") {
          const { note } = item;

          return <li key={index}>{note}</li>;
        }

        return null;
      })}
    </ul>
  </div>
);

export default withSnackbar(CustomizationAreaClaimDetails);
